import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import api from "@/lib/api";
import { BadgeCheck, Download, Image as ImageIcon, LogOut, Gauge, BarChart3, Building2, CircleUser, ClipboardList, Cog, Database, Eye, FileSpreadsheet, FileStack, FileText, History, KeyRound, LayoutDashboard, Network, Palette, Settings, Share2, ShoppingCart, Upload, UserCog, Wallet, Wrench } from "lucide-react";
import { useSignOut } from "@/components/layout/TopBar";
import { promptInstall, useInstall } from "@/lib/pwa";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { cn } from "@/lib/utils";
import { hasApprovals } from "@/components/layout/ProtectedLayout";

const linkCls = ({ isActive }) =>
  cn(
    "group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors duration-150",
    isActive ? "bg-teal-600/15 text-teal-300 font-medium" : "text-slate-300 hover:bg-slate-800 hover:text-white"
  );

const GroupTitle = ({ icon: Icon, children }) => (
  <div className="mb-1.5 flex items-center gap-2 px-2.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">
    {Icon && <Icon className="h-3.5 w-3.5" />}
    {children}
  </div>
);

export const SidebarContent = ({ onNavigate, mobile }) => {
  const signOut = useSignOut();
  const install = useInstall();
  const { user } = useAuth();
  const task = !!user?.is_task_user;
  const grants = task ? user.sections : [];
  const has = (s) => !task || grants.includes(s);
  const [docs, setDocs] = useState(false);
  useEffect(() => { if (user) api.get("/documents").then((r) => setDocs(r.data.documents.length > 0)).catch(() => {}); }, [user]);
  return (
    <div className="flex h-full flex-col text-slate-100">
      <nav className="sidebar-scroll flex-1 space-y-5 overflow-y-auto px-3 py-5" onClick={onNavigate}>
        <div>
          <GroupTitle>Work</GroupTitle>
          <NavLink to="/work" className={linkCls} data-testid="nav-work"><Gauge className="h-4 w-4" /> Tasks &amp; Approvals</NavLink>
          <NavLink to="/tasks" className={linkCls} data-testid="nav-tasks"><ClipboardList className="h-4 w-4" /> Tasks</NavLink>
          {hasApprovals(user) && <NavLink to="/approvals" className={linkCls} data-testid="nav-approvals"><BadgeCheck className="h-4 w-4" /> Approvals</NavLink>}
          {docs && <NavLink to="/documents" className={linkCls} data-testid="nav-documents"><FileText className="h-4 w-4" /> Documents</NavLink>}
          {!mobile && <NavLink to="/profile" className={linkCls} data-testid="nav-profile"><CircleUser className="h-4 w-4" /> Profile</NavLink>}
        </div>
        {(!task || grants.length > 0) && (
        <div>
          <GroupTitle>Overview</GroupTitle>
          {has("dashboard") && <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard"><LayoutDashboard className="h-4 w-4" /> Dashboard</NavLink>}
          {has("sales") && <NavLink to="/sales" className={linkCls} data-testid="nav-sales"><ShoppingCart className="h-4 w-4" /> Sales Analysis</NavLink>}
          {has("outstanding") && <NavLink to="/outstanding" className={linkCls} data-testid="nav-outstanding"><Wallet className="h-4 w-4" /> Outstanding</NavLink>}
          {has("expenses") && <NavLink to="/expenses" className={linkCls} data-testid="nav-expenses"><BarChart3 className="h-4 w-4" /> Expenses</NavLink>}
          {has("cogs") && <NavLink to="/cogs" className={linkCls} data-testid="nav-cogs"><Cog className="h-4 w-4" /> Cost of sales</NavLink>}
          {(has("reports") || has("monthly")) && <NavLink to="/reports" className={linkCls} data-testid="nav-reports"><FileSpreadsheet className="h-4 w-4" /> {task && !has("reports") ? "Monthly P&L" : "Reports"}</NavLink>}
        </div>
        )}
        {!task && (<>
        <div>
          <GroupTitle icon={Database}>Data</GroupTitle>
          <NavLink to="/data/uploads" className={linkCls} data-testid="nav-uploads"><Upload className="h-4 w-4" /> Data Uploads</NavLink>
          <NavLink to="/data/history" className={linkCls} data-testid="nav-history"><History className="h-4 w-4" /> Upload History</NavLink>
          <NavLink to="/data/mapping" className={linkCls} data-testid="nav-mapping"><Network className="h-4 w-4" /> Expense mapping</NavLink>
        </div>
        {user?.is_super_admin && (
          <div data-testid="nav-section-platform">
            <GroupTitle icon={Building2}>Platform</GroupTitle>
            <NavLink to="/admin/companies" className={linkCls} data-testid="nav-companies"><Building2 className="h-4 w-4" /> Companies</NavLink>
            <NavLink to="/admin/data" className={linkCls} data-testid="nav-data-management"><Wrench className="h-4 w-4" /> Data Management</NavLink>
            <NavLink to="/admin/records" className={linkCls} data-testid="nav-all-records"><Database className="h-4 w-4" /> All records</NavLink>
            <NavLink to="/admin/app-icon" className={linkCls} data-testid="nav-app-icon"><ImageIcon className="h-4 w-4" /> App icon</NavLink>
            <NavLink to="/admin/users" className={linkCls} data-testid="nav-all-users"><UserCog className="h-4 w-4" /> All users</NavLink>
            <NavLink to="/admin/visibility" className={linkCls} data-testid="nav-visibility"><Eye className="h-4 w-4" /> Public view defaults</NavLink>
            <NavLink to="/admin/proposals" className={linkCls} data-testid="nav-proposals"><FileStack className="h-4 w-4" /> Proposals</NavLink>
          </div>
        )}
        <div>
          <GroupTitle>Company</GroupTitle>
          <NavLink to="/company/branding" className={linkCls} data-testid="nav-branding"><Palette className="h-4 w-4" /> Branding</NavLink>
          <NavLink to="/company/sharing" className={linkCls} data-testid="nav-sharing"><Share2 className="h-4 w-4" /> Viewer links</NavLink>
          {user?.role === "company_admin" && !user?.is_super_admin && <NavLink to="/company/users-import" className={linkCls} data-testid="nav-users-import"><FileSpreadsheet className="h-4 w-4" /> Import users</NavLink>}
          <NavLink to="/settings" className={linkCls} data-testid="nav-settings"><Settings className="h-4 w-4" /> Settings</NavLink>
        </div>
        </>)}
        {mobile && (
          <div className="border-t border-slate-800 pt-4" data-testid="drawer-account">
            <GroupTitle>Account</GroupTitle>
            <div className="mb-1 px-2.5" data-testid="drawer-user"><div className="truncate text-sm font-semibold text-white">{user?.name || user?.email}</div><div className="truncate text-xs text-slate-400">{user?.role_label || (user?.is_super_admin ? "Super admin" : "Company admin")}</div></div>
            <NavLink to="/profile" end className={(s) => linkCls(s) + " min-h-[44px]"} data-testid="drawer-profile"><CircleUser className="h-4 w-4" /> Profile</NavLink>
            <NavLink to="/profile#trusted-devices" className={linkCls({ isActive: false }) + " min-h-[44px]"} data-testid="drawer-trusted-devices"><KeyRound className="h-4 w-4" /> Trusted devices</NavLink>
            {install && <button type="button" onClick={() => promptInstall()} className={linkCls({ isActive: false }) + " w-full min-h-[44px]"} data-testid="drawer-install"><Download className="h-4 w-4" /> Install app</button>}
            <button type="button" onClick={signOut} className={linkCls({ isActive: false }) + " w-full min-h-[44px] text-red-300"} data-testid="drawer-logout"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        )}
      </nav>
    </div>
  );
};
